import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  Snackbar,
  Slide,
  useTheme,
  LinearProgress
} from '@mui/material';
import { Icon } from '@iconify/react';
import RenderTable from './RenderTable';
import ChatLoader from './ChatLoader';
import ConversationSkeleton from './ConversationSkeleton';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../utils/auth/msalConfig';
import { chatService, userService } from '../services';
import { StopCircle, HourglassBottom, CloudDownload } from '@mui/icons-material';

const suggestedPrompts = [
  "Convert the price of USD to INR",
  "Is the part N5606P08001G11 in inventory? Where is it located, how many and what is the cost?",
  "Have we quoted N5606P08001G11 to any customer before? What was the price?",
];

// Local storage key for storing active session ID and tokens
const SESSION_KEY = "parts360_session_id";
const SESSION_TOKEN_KEY = "parts360_session_tokens";
const MESSAGE_FEEDBACK_KEY = "parts360_message_feedback"; // New key for storing message feedback
const ACTIVE_REQUESTS_KEY = "parts360_active_requests"; // New key for tracking active requests
const PAGE_LOAD_KEY = "parts360_page_load_time"; // Track when page was loaded

// Helper function to get session ID from URL
const getSessionIdFromUrl = () => {
  const urlParts = window.location.pathname.split('/');
  const conversationId = urlParts[urlParts.length - 1];

  // Check if the ID is a valid UUID format that matches backend IDs
  const isValidUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(conversationId);

  if (isValidUuid) {
    return conversationId;
  }
  return null;
};

// Helper function to store session data in localStorage specific to a session ID
const storeSessionToken = (sessionId, token) => {
  if (sessionId && token) {
    try {
      const sessionTokenMap = JSON.parse(localStorage.getItem(SESSION_TOKEN_KEY) || '{}');
      sessionTokenMap[sessionId] = token;
      localStorage.setItem(SESSION_TOKEN_KEY, JSON.stringify(sessionTokenMap));
      console.log(`Stored token for session ${sessionId}`);
    } catch (error) {
      console.error("Error storing session token:", error);
    }
  }
};

// Helper function to store message feedback in localStorage
const storeFeedback = (messageId, feedback) => {
  try {
    // Get existing feedback data
    const feedbackData = JSON.parse(localStorage.getItem(MESSAGE_FEEDBACK_KEY) || '{}');

    // Update with new feedback
    feedbackData[messageId] = feedback;

    // Save back to localStorage
    localStorage.setItem(MESSAGE_FEEDBACK_KEY, JSON.stringify(feedbackData));
    console.log(`Stored feedback for message ${messageId}`);
  } catch (error) {
    console.error("Error storing message feedback:", error);
  }
};

// Helper function to get session token from localStorage
const getSessionToken = (sessionId) => {
  if (!sessionId) return null;
  try {
    const sessionTokenMap = JSON.parse(localStorage.getItem(SESSION_TOKEN_KEY) || '{}');
    return sessionTokenMap[sessionId];
  } catch (error) {
    console.error("Error retrieving session token:", error);
    return null;
  }
};

// Helper function to store active request info
const storeActiveRequest = (requestId, messageId, timestamp) => {
  try {
    const activeRequests = JSON.parse(localStorage.getItem(ACTIVE_REQUESTS_KEY) || '{}');
    const pageLoadTime = localStorage.getItem(PAGE_LOAD_KEY);

    activeRequests[requestId] = {
      messageId,
      timestamp,
      sessionId: window.location.pathname.split('/').pop(),
      pageLoadTime: pageLoadTime || timestamp // Track when the page that started this request was loaded
    };
    localStorage.setItem(ACTIVE_REQUESTS_KEY, JSON.stringify(activeRequests));
    console.log(`Stored active request ${requestId} for message ${messageId}`);
  } catch (error) {
    console.error("Error storing active request:", error);
  }
};

// Helper function to remove active request info
const removeActiveRequest = (requestId) => {
  try {
    const activeRequests = JSON.parse(localStorage.getItem(ACTIVE_REQUESTS_KEY) || '{}');
    delete activeRequests[requestId];
    localStorage.setItem(ACTIVE_REQUESTS_KEY, JSON.stringify(activeRequests));
    console.log(`Removed active request ${requestId}`);
  } catch (error) {
    console.error("Error removing active request:", error);
  }
};

// Helper function to get requests from previous page loads
const getRequestsFromPreviousPageLoad = () => {
  try {
    const activeRequests = JSON.parse(localStorage.getItem(ACTIVE_REQUESTS_KEY) || '{}');
    const currentPageLoadTime = localStorage.getItem(PAGE_LOAD_KEY);
    const previousPageRequests = [];

    Object.entries(activeRequests).forEach(([requestId, info]) => {
      // If the request was started before the current page load, it's from a previous page
      if (currentPageLoadTime && info.pageLoadTime &&
        parseInt(info.pageLoadTime) < parseInt(currentPageLoadTime)) {
        previousPageRequests.push({ requestId, ...info });
      }
    });

    return previousPageRequests;
  } catch (error) {
    console.error("Error getting requests from previous page load:", error);
    return [];
  }
};

// Helper function to get orphaned requests (older than 90 seconds)
const getOrphanedRequests = () => {
  try {
    const activeRequests = JSON.parse(localStorage.getItem(ACTIVE_REQUESTS_KEY) || '{}');
    const now = Date.now();
    const orphanedRequests = [];

    Object.entries(activeRequests).forEach(([requestId, info]) => {
      const age = now - info.timestamp;
      if (age > 90 * 1000) { // 90 seconds - match API timeout
        orphanedRequests.push({ requestId, ...info });
      }
    });

    return orphanedRequests;
  } catch (error) {
    console.error("Error getting orphaned requests:", error);
    return [];
  }
};

// Helper function to check if a request ID is orphaned
const isRequestOrphaned = (requestId) => {
  try {
    const activeRequests = JSON.parse(localStorage.getItem(ACTIVE_REQUESTS_KEY) || '{}');
    const currentPageLoadTime = localStorage.getItem(PAGE_LOAD_KEY);
    const requestInfo = activeRequests[requestId];

    if (!requestInfo) {
      return true; // Request not found, consider it orphaned
    }

    // Check if request was from previous page load
    if (currentPageLoadTime && requestInfo.pageLoadTime &&
      parseInt(requestInfo.pageLoadTime) < parseInt(currentPageLoadTime)) {
      return true;
    }
    // Check if request is too old (use 90 seconds to match API timeout)
    const age = Date.now() - requestInfo.timestamp;
    if (age > 90 * 1000) { // 90 seconds
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error checking if request is orphaned:", error);
    return true; // Default to orphaned if we can't determine
  }
};

// Helper function to clear all active requests
const clearAllActiveRequests = () => {
  try {
    localStorage.removeItem(ACTIVE_REQUESTS_KEY);
    console.log("Cleared all active requests");
  } catch (error) {
    console.error("Error clearing active requests:", error);
  }
};


// Date utility functions
const isToday = (date) => {
  if (!date) return false;
  const today = new Date();
  const compareDate = new Date(date);
  return (
    compareDate.getDate() === today.getDate() &&
    compareDate.getMonth() === today.getMonth() &&
    compareDate.getFullYear() === today.getFullYear()
  );
};

const isYesterday = (date) => {
  if (!date) return false;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const compareDate = new Date(date);
  return (
    compareDate.getDate() === yesterday.getDate() &&
    compareDate.getMonth() === yesterday.getMonth() &&
    compareDate.getFullYear() === yesterday.getFullYear()
  );
};

const formatDate = (date) => {
  if (!date) return "Unknown Date";
  const options = { weekday: 'long', month: 'long', day: 'numeric' };
  return new Date(date).toLocaleDateString(undefined, options);
};

// Function to group messages by date
const groupMessagesByDate = (messages) => {
  const groups = [];
  let currentDate = null;
  let currentGroup = null;

  messages.forEach(msg => {
    // Use timestamp from the message or current date
    const timestamp = msg.timestamp || new Date().toISOString();
    let dateLabel;

    if (isToday(timestamp)) {
      dateLabel = 'Today';
    } else if (isYesterday(timestamp)) {
      dateLabel = 'Yesterday';
    } else {
      dateLabel = formatDate(timestamp);
    }

    // If this is a new date or the first message, create a new group
    if (dateLabel !== currentDate) {
      currentDate = dateLabel;
      currentGroup = {
        date: dateLabel,
        messages: [msg]
      };
      groups.push(currentGroup);
    } else {
      // Add to the current group
      currentGroup.messages.push(msg);
    }
  });

  return groups;
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});


const ChatArea = ({ isMobile, messages, onSendMessage, isAuthenticated, handleSignIn, isSessionLoading, sessionLoadingFailed, pendingSessionId, pendingNewSession, onSessionSwitchDecision, onLoadingChange }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  console.log("ChatArea component initialized with messages:", messages);
  const [inputValue, setInputValue] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const activeRequestsRef = useRef(new Map()); // Use ref instead of state to prevent re-renders
  const abortedMessagesRef = useRef(new Set()); // Track messages that have been marked as aborted
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [currentMessageId, setCurrentMessageId] = useState(null);
  const [agentType, setAgentType] = useState("sqlagent"); // Default to SQL Agent
  const [isPivotSelected, setIsPivotSelected] = useState(false); // Track pivot selection
  const [copiedMessageIds, setCopiedMessageIds] = useState({}); // Track copied message IDs with timestamps
  const [hasPortalAccess, setHasPortalAccess] = useState(null); // null = checking, true = has access, false = no access
  const [portalAccessLoading, setPortalAccessLoading] = useState(true);
  const [portalAccessError, setPortalAccessError] = useState(null); // Store API error message
  const [feedbackLoading, setFeedbackLoading] = useState({}); // Track loading state for feedback by message ID
  const [messageFeedback, setMessageFeedback] = useState(() => {
    // Try to load feedback from localStorage when component initializes
    try {
      const storedFeedback = localStorage.getItem(MESSAGE_FEEDBACK_KEY);
      return storedFeedback ? JSON.parse(storedFeedback) : {};
    } catch (error) {
      console.error("Error loading message feedback from localStorage:", error);
      return {};
    }
  }); // Store feedback by message ID
  // Snackbar for new conversation notifications
  const [newConversationSnackbar, setNewConversationSnackbar] = useState({
    open: false,
    message: ''
  });

  const [currentRequestId, setCurrentRequestId] = useState(null);
  // State for showing the popover/dialog when user attempts to navigate away
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [nextSessionId, setNextSessionId] = useState(null);

  // Add state for feedback errors
  const [feedbackErrors, setFeedbackErrors] = useState({}); // Store error state by message ID
  const [feedbackSnackbar, setFeedbackSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  const [sessionId, setSessionId] = useState(() => {
    // Try to get current conversation ID from the URL
    const urlParts = window.location.pathname.split('/');
    const conversationId = urlParts[urlParts.length - 1];

    // Check if the ID is a valid UUID format that matches backend IDs
    const isValidUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(conversationId);

    if (isValidUuid) {
      console.log("Using conversation ID from URL:", conversationId);
      return conversationId;
    }

    // If no valid UUID in URL, check localStorage
    const savedSessionId = localStorage.getItem(SESSION_KEY);
    console.log("Retrieved session ID from localStorage:", savedSessionId);
    return savedSessionId;
  });
  const [sessionToken, setSessionToken] = useState(() => {
    const savedSessionId = localStorage.getItem(SESSION_KEY);
    return getSessionToken(savedSessionId);
  });

  // Group messages by date
  const groupedMessages = useMemo(() => {
    // Add timestamps to messages if they don't have them
    const messagesWithTimestamps = messages.map(msg => {
      if (!msg.timestamp) {
        return {
          ...msg,
          timestamp: new Date().toISOString() // default to current time if no timestamp
        };
      }
      return msg;
    });

    return groupMessagesByDate(messagesWithTimestamps);
  }, [messages]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const isInitialLoad = useRef(true); // Track if this is the initial component load
  const { instance, accounts } = useMsal();  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > 0) {
      // Determine delay and behavior based on whether this is initial load or new message
      const delay = isInitialLoad.current ? 300 : 100; // Longer delay for initial load
      const behavior = isInitialLoad.current ? "auto" : "smooth"; // Immediate scroll on reload, smooth for new messages

      const timeoutId = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior });
        if (isInitialLoad.current) {
          isInitialLoad.current = false; // Mark that initial load is complete
        }
      }, delay);

      return () => clearTimeout(timeoutId);
    }
  }, [messages]);// Set page load time when component mounts
  useEffect(() => {
    const pageLoadTime = Date.now().toString();
    localStorage.setItem(PAGE_LOAD_KEY, pageLoadTime);
    console.log("Page loaded at:", pageLoadTime);
  }, []);

  // Handle orphaned loading messages on component mount/reload
  useEffect(() => {
    // Check for loading messages that might be stuck due to page reload
    const loadingMessages = messages.filter(msg => msg.isLoading);
    const orphanedRequests = getOrphanedRequests();
    const previousPageRequests = getRequestsFromPreviousPageLoad();

    // Combine all types of orphaned requests
    const allOrphanedRequests = [...orphanedRequests, ...previousPageRequests];

    if (loadingMessages.length > 0 || allOrphanedRequests.length > 0) {
      console.log("Found orphaned loading messages after reload:", loadingMessages.length);
      console.log("Found orphaned requests:", orphanedRequests.length);
      console.log("Found requests from previous page load:", previousPageRequests.length);

      // Mark these messages as aborted immediately to prevent race conditions
      loadingMessages.forEach(loadingMsg => {
        if (loadingMsg.id) {
          abortedMessagesRef.current.add(loadingMsg.id);
        }
      });
      // Use setTimeout to defer state updates and avoid setState during render
      setTimeout(() => {
        // Replace loading messages with "Request aborted" messages
        loadingMessages.forEach(loadingMsg => {
          const abortedMsg = {
            ...loadingMsg,
            text: 'Request was aborted due to page reload. Please try your query again.',
            isLoading: false,
            hasError: true,
            aborted: true, // Mark as aborted
            timestamp: new Date().toISOString()
          };
          onSendMessage(abortedMsg);
        });
      }, 0);

      // Clean up orphaned request tracking
      allOrphanedRequests.forEach(({ requestId }) => {
        removeActiveRequest(requestId);
      });
    }

    // Clean up any very old requests on app start
    const now = Date.now();
    try {
      const activeRequests = JSON.parse(localStorage.getItem(ACTIVE_REQUESTS_KEY) || '{}');
      let hasChanges = false;

      Object.entries(activeRequests).forEach(([requestId, info]) => {
        const age = now - info.timestamp;
        if (age > 10 * 60 * 1000) { // 10 minutes - very old requests
          delete activeRequests[requestId];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        localStorage.setItem(ACTIVE_REQUESTS_KEY, JSON.stringify(activeRequests));
        console.log("Cleaned up very old request tracking");
      }
    } catch (error) {
      console.error("Error cleaning up old requests:", error);
    }
  }, []); // Run only once on component mount  // Handle beforeunload event to cleanup loading states
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Check if there are pending queries
      if (isLoading && activeRequestsRef.current.size > 0) {
        // Show confirmation dialog to prevent accidental reload
        const message = 'You have a query in progress. Reloading now will cancel the current operation. Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }

      // Set a flag in sessionStorage to indicate that page is being unloaded
      sessionStorage.setItem('parts360_page_unloading', 'true');

      // Cancel all active requests
      activeRequestsRef.current.forEach((abortController, requestId) => {
        console.log(`Cancelling request ${requestId} due to page unload`);
        abortController.abort();
        removeActiveRequest(requestId);
      });
    };

    const handleLoad = () => {
      // Check if page was reloaded
      const wasUnloading = sessionStorage.getItem('parts360_page_unloading');
      if (wasUnloading) {
        console.log("Page reload detected");
        sessionStorage.removeItem('parts360_page_unloading');
      }
    };

    // Handle keyboard shortcuts for reload
    const handleKeyDown = (event) => {
      // Prevent F5 and Ctrl+R when there are pending queries
      if (isLoading && activeRequestsRef.current.size > 0) {
        if (event.key === 'F5' || (event.ctrlKey && event.key === 'r')) {
          event.preventDefault();
          const confirmed = window.confirm(
            'You have a query in progress. Reloading now will cancel the current operation. Do you want to continue?'
          );
          if (confirmed) {
            window.location.reload();
          }
          return false;
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('load', handleLoad);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLoading]); // Include isLoading in dependencies to update handler when loading state changes

  // Cleanup active requests on unmount
  useEffect(() => {    // Set up periodic cleanup of old request tracking
    const cleanupInterval = setInterval(() => {
      const orphanedRequests = getOrphanedRequests();
      if (orphanedRequests.length > 0) {
        console.log(`Cleaning up ${orphanedRequests.length} orphaned request(s)`);
        orphanedRequests.forEach(({ requestId }) => {
          removeActiveRequest(requestId);
        });
      }

      // Also clean up old aborted message IDs (older than 10 minutes)
      const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
      const currentAbortedMessages = Array.from(abortedMessagesRef.current);
      const messagesToRemove = currentAbortedMessages.filter(messageId => {
        // Message IDs are timestamps, so we can compare directly
        return messageId < tenMinutesAgo;
      });

      if (messagesToRemove.length > 0) {
        console.log(`Cleaning up ${messagesToRemove.length} old aborted message ID(s)`);
        messagesToRemove.forEach(messageId => {
          abortedMessagesRef.current.delete(messageId);
        });
      }
    }, 60000); // Check every minute

    return () => {
      clearInterval(cleanupInterval);

      // Cancel all active requests when component unmounts
      activeRequestsRef.current.forEach((abortController, requestId) => {
        console.log(`Cancelling request ${requestId} on unmount`);
        abortController.abort();
        removeActiveRequest(requestId);
      });
      activeRequestsRef.current.clear();
    };
  }, []); // Remove activeRequests dependency

  // Reset session data when switching to a new conversation (messages array is empty)
  useEffect(() => {
    if (messages.length === 0) {
      console.log("New conversation detected, resetting session data");
      setSessionId(null);
      setSessionToken(null);
      localStorage.removeItem(SESSION_KEY);
    }
  }, [messages]);

  // Save session ID to localStorage whenever it changes
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem(SESSION_KEY, sessionId);
    }
  }, [sessionId]);

  // Save session token to localStorage whenever it changes
  useEffect(() => {
    if (sessionId && sessionToken) {
      storeSessionToken(sessionId, sessionToken);
    }
  }, [sessionId, sessionToken]);

  // Load the correct session token when sessionId changes (e.g. when navigating between sessions)
  useEffect(() => {
    if (sessionId) {
      const token = getSessionToken(sessionId);
      if (token) {
        console.log(`Loaded token for session ${sessionId}`);
        setSessionToken(token);
      }
    }
  }, [sessionId]);  // Check portal access when user is authenticated
  useEffect(() => {
    const checkPortalAccess = async () => {
      if (isAuthenticated && accounts.length > 0) {
        try {
          setPortalAccessLoading(true);
          setPortalAccessError(null); // Clear previous errors
          const token = await getAuthToken();
          if (token) {
            const response = await userService.checkPortalAccess(token);
            setHasPortalAccess(response.hasPortalAccess || false);
          } else {
            setHasPortalAccess(false);
          }
        } catch (error) {
          console.error('Error checking portal access:', error);

          // Determine if this is an API connectivity issue or access denial
          let errorMessage = 'Unable to verify access permissions. ';
          if (error.message.includes('Backend service is currently unavailable') ||
            error.message.includes('Unable to connect to the backend')) {
            errorMessage += 'Backend service is currently unavailable. Please try again in a few moments.';
          } else if (error.message.includes('timeout')) {
            errorMessage += 'Request timed out. Please check your connection and try again.';
          } else if (error.message.includes('Internal server error')) {
            errorMessage += 'Server is experiencing issues. Please try again later.';
          } else {
            errorMessage += 'Please try again later.';
          }

          setPortalAccessError(errorMessage);
          setHasPortalAccess(false);
        } finally {
          setPortalAccessLoading(false);
        }
      } else if (!isAuthenticated) {
        setHasPortalAccess(null);
        setPortalAccessLoading(false);
        setPortalAccessError(null);
      }
    };

    checkPortalAccess();
  }, [isAuthenticated, accounts]);

  // Get authentication token for API calls
  const getAuthToken = async () => {
    if (accounts.length > 0) {
      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        });
        return response.accessToken;
      } catch (error) {
        // If silent token acquisition fails, fallback to interactive method
        console.log("Silent token acquisition failed, falling back to interactive");
        try {
          const response = await instance.acquireTokenPopup(loginRequest);
          return response.accessToken;
        } catch (err) {
          console.error("Error acquiring token:", err);
          return null;
        }
      }
    }
    return null;
  };  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Extract the base64 data after the data:application/...;base64, prefix
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => {
        console.error('Error converting file to base64:', error);
        reject(error);
      };
    });
  };  // Function to make API call
  const fetchQueryResponse = async (query, loadingMessageId = null) => {
    setIsLoading(true);

    // Create an AbortController for this request
    const abortController = new AbortController();

    // Store the abort controller for cleanup
    const requestId = Date.now();
    activeRequestsRef.current.set(requestId, abortController);
    setCurrentRequestId(requestId);

    // Track this request in localStorage
    if (loadingMessageId) {
      storeActiveRequest(requestId, loadingMessageId, requestId);
    }

    try {
      const token = await getAuthToken();

      // Always get the most current session ID from the URL
      const urlSessionId = getSessionIdFromUrl();

      // If URL has valid session ID, use it and its corresponding token
      let currentSessionId = urlSessionId || sessionId;

      // Process excel attachment if any
      let excel_data = null;
      if (attachments.length > 0) {
        // We only use the first attachment for now
        const firstAttachment = attachments[0];
        try {
          // Convert file to base64
          excel_data = await fileToBase64(firstAttachment.file);
          console.log(`Converted ${firstAttachment.name} to base64`);
        } catch (error) {
          console.error("Error processing attachment:", error);
        }
      }

      console.log("Sending query with session details:", {
        sessionId: currentSessionId,
        hasExcelData: !!excel_data,
        query: query.substring(0, 50) + (query.length > 50 ? '...' : '')
      });      // Use chat service with abort signal
      const responseData = await chatService.sendQuery({
        query,
        agentType,
        sessionId: currentSessionId,
        token,
        excel_data,
        pivot_enabled: isPivotSelected, // Pass pivot selection
        signal: abortController.signal // Pass abort signal to the service
      });

      console.log("Received response from backend:", responseData, currentSessionId);
      // Log session handling details
      if (responseData.session_id) {
        if (responseData.session_id !== currentSessionId) {
          console.log("🔄 Backend returned different session_id:", {
            sent: currentSessionId,
            received: responseData.session_id,
            action: "Starting new conversation"
          });
        } else {
          console.log("✅ Session continued:", responseData.session_id);
        }
      }

      return responseData;
    } catch (error) {
      console.error("Error fetching data:", error);

      // Check if the request was aborted first
      if (error.name === 'AbortError') {
        console.log("Request was aborted");
        return {
          textual_response: 'Request was cancelled.',
          results: null,
          error: true,
          aborted: true
        };
      }

      // Provide more user-friendly error messages for server errors
      let errorMessage = '';
      if (error.message.includes('Backend service is currently unavailable') ||
        error.message.includes('Unable to connect to the backend')) {
        errorMessage = 'Backend service is currently down. Please try again in a few moments.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'The request is taking longer than expected. Please try again.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to the backend service. Please check your internet connection and try again.';
      } else if (error.message.includes('Internal server error') || error.message.includes('500')) {
        errorMessage = 'A server error occurred while processing your request. Please try again or contact support if the issue persists.';
      } else {
        errorMessage = error.message || "An unexpected error occurred while processing your request.";
      }

      // Only check if request is orphaned after handling other error types
      // This prevents legitimate server errors from being masked as "page reload" errors
      if (isRequestOrphaned(requestId)) {
        console.log("Request detected as orphaned during error handling");
        return {
          textual_response: 'Request was aborted. Please try your query again.',
          results: null,
          error: true,
          aborted: true
        };
      }
      return {
        textual_response: errorMessage,
        results: null,
        error: true
      };
    } finally {
      // Don't clear loading state here - let handleSend manage it after processing the response
      // setIsLoading(false);
      // Clean up the request from activeRequests
      activeRequestsRef.current.delete(requestId);

      // Clean up request tracking from localStorage
      removeActiveRequest(requestId);
      setCurrentRequestId(null);
    }
  };

  const handleStop = () => {
    if (currentRequestId != null) {
      const controller = activeRequestsRef.current.get(currentRequestId);
      if (controller) {
        controller.abort();
        // clean up
        removeActiveRequest(currentRequestId);
        activeRequestsRef.current.delete(currentRequestId);
      }
      setIsLoading(false);
      setCurrentRequestId(null);
    }
  };

  useEffect(() => {
    // whenever the user has chosen to switch sessions (nextSessionId != null)
    // but we're still loading, pop up the Stop/Continue dialog
    if (isLoading && currentRequestId != null && nextSessionId !== null) {
      setStopDialogOpen(true);
    }
  }, [nextSessionId, isLoading, currentRequestId]);

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  // Handle pending session switch or new session from parent
  useEffect(() => {
    if ((pendingSessionId || pendingNewSession)) {
      if (isLoading && currentRequestId != null) {
        setStopDialogOpen(true);
      } else if (!isLoading && currentRequestId == null && !stopDialogOpen) {
        // No active request, proceed immediately, but only if dialog is not open
        if (onSessionSwitchDecision) {
          if (pendingSessionId) {
            onSessionSwitchDecision('switch', pendingSessionId);
          } else if (pendingNewSession) {
            onSessionSwitchDecision('switch', null);
          }
        }
      }
    }
    // eslint-disable-next-line
  }, [pendingSessionId, pendingNewSession, isLoading, currentRequestId, stopDialogOpen]);

  // If dialog is open and query finishes, close dialog and clear pending switch
  useEffect(() => {
    if (stopDialogOpen && !isLoading) {
      setStopDialogOpen(false);
      if (onSessionSwitchDecision) {
        onSessionSwitchDecision('continue'); // Clear pending state, do not redirect
      }
    }
  }, [isLoading, stopDialogOpen, onSessionSwitchDecision]);

  const sendFeedback = async (sessionId, questionId, isLiked, feedbackDescription = "") => {
    try {
      const token = await getAuthToken();

      // Use chat service
      const success = await chatService.sendFeedback({
        sessionId,
        questionId,
        isLiked,
        feedbackDescription,
        token
      }); if (success) {
        return { success: true };
      } else {
        throw new Error('Failed to send feedback to server');
      }
    } catch (error) {
      console.error("Error sending feedback:", error);

      // Determine user-friendly error message
      let errorMessage = 'Failed to send feedback. ';
      if (error.message.includes('Backend service is currently unavailable') ||
        error.message.includes('Unable to connect to the backend')) {
        errorMessage += 'Please check your connection and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage += 'Request timed out. Please try again.';
      } else {
        errorMessage += 'Please try again later.';
      }

      return { success: false, error: errorMessage };
    }
  };
  const handleSend = async () => {
    if (!inputValue.trim() && attachments.length === 0) return;

    // If user is not authenticated, trigger sign-in
    if (!isAuthenticated) {
      handleSignIn();
      return;
    }

    // Check if user has portal access
    if (hasPortalAccess === false) {
      console.log('User does not have portal access');
      return; // Silently prevent sending - UI will show access denied message
    }

    try {
      // Create appropriate text for attachment-only messages
      const displayText = inputValue.trim()
        ? inputValue
        : attachments.length === 1
          ? `Analyzing file: ${attachments[0].name}`
          : `Analyzing ${attachments.length} files`;

    const userMsg = {

      id: Date.now(),

      sender: 'user',

      text: displayText,

      attachments,

      timestamp: new Date().toISOString()

    };

    onSendMessage(userMsg);



    // If there's no query text but there are attachments, create a generic query about the file

    const query = inputValue.trim() || (attachments.length > 0 ? "Please analyze this file" : "");

    setInputValue('');

    setAttachments([]);

    setIsPivotSelected(false); // Reset pivot selection after sending

      // Set loading state without adding a loading message to chat
      setIsLoading(true);
      const loadingMsgId = Date.now() + 1;

      // Make API call with loading message ID for tracking
      const responseData = await fetchQueryResponse(query, loadingMsgId);

      console.log("responsedata", responseData)

      // Check if backend returned a different session_id than what we sent
      const sentSessionId = getSessionIdFromUrl() || sessionId;
      const receivedSessionId = responseData.session_id;
      const isNewSession = receivedSessionId && sentSessionId && receivedSessionId !== sentSessionId; console.log("Session comparison:", {
        sent: sentSessionId,
        received: receivedSessionId,
        isNewSession
      });

      // Declare navigation variables at the proper scope
      let navigationUrl = null;      // Store session_id if received from backend
      if (responseData.session_id) {
        const currentUrlSessionId = getSessionIdFromUrl();
        const isCurrentlyUsingTempId = currentUrlSessionId && currentUrlSessionId.startsWith('temp-');
        const hasNoSessionInUrl = !currentUrlSessionId;

        console.log("Session ID processing:", {
          currentUrlSessionId,
          isCurrentlyUsingTempId,
          hasNoSessionInUrl,
          receivedSessionId: responseData.session_id
        });

        setSessionId(responseData.session_id);

        // If this is a new session (different from what we sent), log it and notify user
        if (isNewSession) {
          console.log("Backend returned new session - starting fresh conversation");
          console.log(`Previous session: ${sentSessionId} -> New session: ${receivedSessionId}`);

          // Show notification to user
          setNewConversationSnackbar({
            open: true,
            message: "New conversation started"
          });
        }

        // Store navigation info but don't navigate yet - wait until after message processing
        // Navigate if:
        // 1. We're using a temp ID and backend returned a real session ID, OR
        // 2. We have no session ID in URL and backend returned a session ID (new conversation)
        const shouldNavigateAfterProcessing = (isCurrentlyUsingTempId && responseData.session_id) ||
          (hasNoSessionInUrl && responseData.session_id);
        navigationUrl = shouldNavigateAfterProcessing ? `/conversation/${responseData.session_id}` : null;

        console.log("Navigation decision:", {
          shouldNavigateAfterProcessing,
          navigationUrl
        });
      }
      if (responseData.token) {
        setSessionToken(responseData.token);
      }

      // Check for error in the response
      let responseText = '';
      let responseType = ['text'];
      let hasError = false;

      // Prefer any explicit fetch‐error or backend error message
      if (responseData.error) {
        hasError = true;

        // 1) raw_response is set on fetch/network errors
        console.log('Error response:', responseData);
        if (responseData.textual_response) {
          responseText = responseData.textual_response;
        }
        // 2) backend SQL errors often come back as a string in .error
        else if (typeof responseData.error === 'string') {
          responseText = `There was an error executing the SQL query.`;
        }
        // 3) Just in case
        else {
          responseText = 'An unexpected error occurred while processing your request.';
        }
        responseType = ['text'];
      } else {
        // No error → show the normal text, or default if empty
        responseText = responseData.textual_response || '';
        responseType = Array.isArray(responseData.results)
          ? ['table', 'text']
          : ['text'];
      }

      // Check if this message was already marked as aborted (due to page reload)
      if (abortedMessagesRef.current.has(loadingMsgId)) {
        console.log(`Message ${loadingMsgId} was already marked as aborted, skipping response processing`);
        // Ensure loading state is cleared even for aborted messages
        setIsLoading(false);
        return;
      }      // Now build your AI message
      const aiMsg = {
        id: loadingMsgId,
        sender: 'ai',
        sql_text: responseData.sql_query,
        text: responseText,
        type: responseType,
        tableData: responseData.results,
        isLoading: false,
        hasError,
        aborted: responseData.aborted || false, // Include aborted flag
        token: responseData.token,
        sessionContext: responseData.session_context,
        session_id: responseData.session_id || sentSessionId || sessionId, // Use fallback session ID
        session_name: responseData.session_name,
        question_id: responseData.question_id, // Keep as is - will be handled in feedback function
        timestamp: new Date().toISOString()
      };

      console.log("DEBUG: About to call onSendMessage with AI message:");
      console.log("DEBUG: AI message ID:", aiMsg.id);
      console.log("DEBUG: AI message sender:", aiMsg.sender);
      console.log("DEBUG: AI message isLoading:", aiMsg.isLoading);
      console.log("DEBUG: Loading message ID was:", loadingMsgId);
      onSendMessage(aiMsg);

      console.log("DEBUG: onSendMessage call completed");

      // Clear loading state after successfully processing and sending the AI message
      setIsLoading(false);

      // Clean up the aborted message tracking since we successfully processed this message
      abortedMessagesRef.current.delete(loadingMsgId);

      // Now it's safe to navigate if needed (after message processing is complete)
      console.log("Final navigation check:", { navigationUrl });
      if (navigationUrl) {
        console.log(`Updating URL from temp ID to real session ID: ${navigationUrl}`);
        setTimeout(() => {
          window.history.replaceState({}, '', navigationUrl);
        }, 100); // Small delay to ensure message has been rendered
      } else {
        console.log("No navigation needed");
      }
    } catch (error) {
      console.error('Error in handleSend:', error);
      // Ensure loading state is cleared even if there's an error in message processing
      setIsLoading(false);
    }
  };

  const handleInputChange = e => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePivotToggle = () => {
    setIsPivotSelected(prev => !prev);
  };

  const handleSuggestedPromptClick = prompt => {
    setInputValue(prompt);
    inputRef.current?.focus();
  }; const handleAddAttachmentClick = async () => {
    if (hasPortalAccess === false) {
      return; // Prevent attachment upload for users without portal access
    }

    // If not authenticated, trigger sign-in before allowing file upload
    if (!isAuthenticated) {
      handleSignIn();
      return;
    }

    // Trigger file selection dialog
    fileInputRef.current?.click();
  };
  const handleFileChange = e => {
    const files = Array.from(e.target.files);
    if (files.length) {
      // Convert files to our attachment format with a name property
      const processedFiles = files.map(file => ({
        name: file.name,
        file: file,
        base64Data: null // Will be populated before sending
      }));
      setAttachments(prev => [...prev, ...processedFiles]);
    }
    e.target.value = null; // allow re-upload same file
  };

  const handleRemoveAttachment = index => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }; const handleLike = async (messageId) => {
    // Find the message to get its associated question_id if available
    const message = messages.find(msg => msg.id === messageId);

    // Set loading state for this message
    setFeedbackLoading(prev => ({
      ...prev,
      [messageId]: true
    }));

    // If this is an AI message with question_id, send feedback to the API first
    if (message && message.sender === 'ai') {
      const questionId = message.question_id || 1; // Default to 1 if not provided
      const currentSessionId = message.session_id || sessionId;

      if (currentSessionId) {
        // Send positive feedback to API
        const result = await sendFeedback(currentSessionId, questionId, true);

        if (result.success) {
          // Only update UI and localStorage if API call was successful
          const newFeedbackState = { liked: true, disliked: false };
          setMessageFeedback(prev => ({
            ...prev,
            [messageId]: newFeedbackState
          }));

          // Store feedback in localStorage only on success
          storeFeedback(messageId, newFeedbackState);

          // Clear any previous error for this message
          setFeedbackErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[messageId];
            return newErrors;
          });
        } else {
          // If feedback failed, show error state but don't update localStorage
          setFeedbackErrors(prev => ({
            ...prev,
            [messageId]: result.error
          }));

          setFeedbackSnackbar({
            open: true,
            message: result.error,
            severity: 'error'
          });
        }
      }
    } else {
      // For non-AI messages or if no session, just update locally
      const newFeedbackState = { liked: true, disliked: false };
      setMessageFeedback(prev => ({
        ...prev,
        [messageId]: newFeedbackState
      }));
      storeFeedback(messageId, newFeedbackState);
    }

    // Clear loading state
    setFeedbackLoading(prev => ({
      ...prev,
      [messageId]: false
    }));
  }; const handleDislike = (messageId) => {
    // Find the message to reference later when submitting feedback
    const message = messages.find(msg => msg.id === messageId);

    // Only allow feedback for AI messages
    if (message && message.sender === 'ai') {
      setCurrentMessageId(messageId);
      setFeedbackDialog(true);

      // We don't store feedback in localStorage yet - that happens after 
      // the user submits their feedback text in the dialog
    }
  };

  const handleDialogClose = () => {
    setFeedbackDialog(false);
    setFeedbackText('');
    setCurrentMessageId(null);
  }; const handleFeedbackSubmit = async () => {
    if (currentMessageId) {
      // Set loading state for this message
      setFeedbackLoading(prev => ({
        ...prev,
        [currentMessageId]: true
      }));

      // Find the message to get session and question IDs
      const message = messages.find(msg => msg.id === currentMessageId);

      // If this is an AI message, send feedback to the API first
      if (message && message.sender === 'ai') {
        const questionId = message.question_id || 1; // Default to 1 if not provided
        const currentSessionId = message.session_id || sessionId;

        if (currentSessionId) {
          // Send negative feedback with description to API
          const result = await sendFeedback(currentSessionId, questionId, false, feedbackText);

          if (result.success) {
            // Only update UI and localStorage if API call was successful
            const newFeedbackState = {
              liked: false,
              disliked: true,
              feedback: feedbackText
            };

            // Update UI state for immediate feedback
            setMessageFeedback(prev => ({
              ...prev,
              [currentMessageId]: newFeedbackState
            }));

            // Store feedback in localStorage only on success
            storeFeedback(currentMessageId, newFeedbackState);

            // Clear any previous error for this message
            setFeedbackErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[currentMessageId];
              return newErrors;
            });
            // Clear any previous error for this message
            setFeedbackErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[currentMessageId];
              return newErrors;
            });
          } else {
            // If feedback failed, show error state but don't update localStorage
            setFeedbackErrors(prev => ({
              ...prev,
              [currentMessageId]: result.error
            }));

            setFeedbackSnackbar({
              open: true,
              message: result.error,
              severity: 'error'
            });
          }
        }
      } else {
        // For non-AI messages, just update locally
        const newFeedbackState = {
          liked: false,
          disliked: true,
          feedback: feedbackText
        };
        setMessageFeedback(prev => ({
          ...prev,
          [currentMessageId]: newFeedbackState
        }));
        storeFeedback(currentMessageId, newFeedbackState);
      }

      // Clear loading state
      setFeedbackLoading(prev => ({
        ...prev,
        [currentMessageId]: false
      }));
    }

    // Close the dialog
    handleDialogClose();
  };

  // Format text with markdown-like bold syntax (** **)
  const formatMessageText = (text) => {
    if (!text) return '';

    // Replace text between ** ** with bold spans
    // Using a regex with positive lookahead and lookbehind to match text between ** **
    return text.split(/(\*\*.*?\*\*)/g).map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // Extract the text between ** and **
        const boldText = part.substring(2, part.length - 2);
        return <strong key={index}>{boldText}</strong>;
      }
      return part;
    });
  };

  const handleCopy = (text, messageId) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        console.log('Text copied to clipboard');

        // Set the copied state for this message
        setCopiedMessageIds(prev => ({
          ...prev,
          [messageId]: true
        }));

        // Clear the copied state after 3 seconds
        setTimeout(() => {
          setCopiedMessageIds(prev => {
            const newState = { ...prev };
            delete newState[messageId];
            return newState;
          });
        }, 3000);
      })
      .catch(err => {
        console.error('Error copying text: ', err);
      });
  };
  console.log("Grouped Messages: ", groupedMessages);
  // Show loading state while checking portal access
  if (portalAccessLoading) {
    return (
      <Box
        sx={{
          bgcolor: 'background.sidebar',
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 2px)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#2065D1', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Checking access permissions...
          </Typography>
        </Box>
      </Box>
    );
  }  // Show session loading state
  if (isSessionLoading) {
    return (
      <Box
        sx={{
          bgcolor: 'background.sidebar',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          height: 'calc(100vh - 2px)',
          pt: isMobile ? 2 : 4, // Less padding on mobile
          px: isMobile ? 2 : 12, // Much less horizontal padding on mobile
          overflow: 'hidden',
        }}      >
        <ConversationSkeleton isMobile={isMobile} />
      </Box>
    );
  }
  // For failed sessions, we don't show an error dialog anymore - instead we allow
  // the user to use the default input interface by falling through to the main UI
  // This provides a better user experience than showing an error message

  // Show access denied message for users without portal access
  if (isAuthenticated && hasPortalAccess === false) {
    const isApiError = portalAccessError !== null;

    return (
      <Box
        sx={{
          bgcolor: 'background.sidebar',
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 2px)',
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: '16px',
            textAlign: 'center',
            maxWidth: '500px',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {isApiError ? (
            <>
              <Icon
                icon="material-symbols:cloud-off"
                width={64}
                height={64}
                style={{ color: '#ff9800', marginBottom: '16px' }}
              />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                Backend Service Unavailable
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                {portalAccessError}
              </Typography>
              <Alert severity="warning" sx={{ mt: 2, borderRadius: '8px' }}>
                <Typography variant="body2">
                  Please wait a moment and refresh the page, or contact support if the issue persists.
                </Typography>
              </Alert>
              <Button
                variant="outlined"
                onClick={() => {
                  if (isLoading) {
                    const confirmed = window.confirm(
                      'You have a query in progress. Reloading now will cancel the current operation. Do you want to continue?'
                    );
                    if (!confirmed) {
                      return;
                    }
                  }
                  window.location.reload();
                }}
                sx={{
                  mt: 3,
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 3,
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  '&:hover': {
                    borderColor: '#f57c00',
                    bgcolor: 'rgba(255, 152, 0, 0.04)'
                  }
                }}
                startIcon={<Icon icon="material-symbols:refresh" />}
              >
                Retry Connection
              </Button>
            </>
          ) : (
            <>
              <Icon
                icon="material-symbols:lock-outline"
                width={64}
                height={64}
                style={{ color: '#ff6b6b', marginBottom: '16px' }}
              />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                Access Restricted
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                You don't have permission to access the chat portal. Please contact your administrator to request access.
              </Typography>
              <Alert severity="info" sx={{ mt: 2, borderRadius: '8px' }}>
                <Typography variant="body2">
                  Your account is authenticated but doesn't have portal access enabled.
                </Typography>
              </Alert>
            </>
          )}
        </Paper>
      </Box>
    );
  }
  return (
    <Box
      data-tour="chat-area"
      sx={{
        bgcolor: 'background.sidebar',
        flexGrow: 1,
        display: 'flex',
        padding: 0,
        height: 'calc(100vh - 2px)',
        overflow: 'hidden',
      }}
    >

      <Dialog
        open={stopDialogOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setStopDialogOpen(false)}
        PaperProps={{
          sx: {
            p: 0,
            borderRadius: 3,
            background: theme.palette.background.paper,
            boxShadow: theme.shadows[16],
            minWidth: 380,
          }
        }}
      >
        {/* Header with icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            p: 2,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          <CloudDownload fontSize="large" />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Fetching Response
          </Typography>
        </Box>

        <DialogContent sx={{ pt: 2, pb: 1, px: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            This operation can take a few seconds to complete.
          </Typography>

          <Box sx={{ my: 2 }}>
            <LinearProgress />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            You can continue waiting for the result, or abort now and switch to another session.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 1, py: 1 }}>
          <Button
            startIcon={<HourglassBottom />}
            variant="outlined"
            onClick={() => {
              setStopDialogOpen(false);
              onSessionSwitchDecision?.('continue');
            }}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Continue Waiting
          </Button>

          <Button
            startIcon={<StopCircle />}
            variant="contained"
            color="error"
            onClick={() => {
              handleStop();
              setStopDialogOpen(false);
              if (onSessionSwitchDecision) {
                if (pendingSessionId) {
                  onSessionSwitchDecision('switch', pendingSessionId);
                } else if (pendingNewSession) {
                  onSessionSwitchDecision('switch', null);
                }
              }
            }}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Stop &amp; Switch
          </Button>
        </DialogActions>
      </Dialog>
      

      
      <input
        type="file"
        hidden
        ref={fileInputRef}
        onChange={handleFileChange}
        // Accept Excel files for upload (backend handles processing, not using xlsx library in frontend)
        accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '20px',
          justifyContent: messages.length ? 'normal' : 'center',
          flexGrow: 1,
          border: '1px solid #24293a',
          borderColor: 'background.paper',
          bgcolor: 'background.default',
          overflow: 'hidden',
          height: '100%',
        }}      >        {/* Message List Area */}
        {(messages.length > 0 || isLoading) && (
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 0,
              pt: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0,0,0,0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(0,0,0,0.5)',
                },
              },
            }}
          >
            <Box
              sx={{
                width: isMobile ? '100%' : '80%',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                gap: 2,
                p: isMobile ? 0 : 3,
              }}
            >
              {/* Render messages grouped by date */}
              {groupedMessages.map((group, groupIndex) => (
                <React.Fragment key={`group-${groupIndex}`}>
                  <Divider textAlign="center" sx={{ mb: 2, mt: groupIndex > 0 ? 2 : 0 }}>
                    {group.date}
                  </Divider>

                  {group.messages.map(msg => (
                    <Box
                      key={msg.id}
                      sx={{
                        display: 'flex',
                        justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: '12px',
                          backgroundColor:
                            msg.sender === 'user' ? 'background.sender' : 'transparent',
                          maxWidth: '100%',
                          wordWrap: 'break-word',
                        }}
                      >
                        {(msg.sender === 'ai') && (
                          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexDirection: 'row' }}>
                            <img src="/ethos.svg" alt="Logo" width={20} height={20} />
                            <Typography variant="h6" sx={{ fontSize: "15px", fontWeight: "bold", color: "text.primary" }}>
                              Parts360
                            </Typography>
                          </Box>)}
                        {!msg.isLoading && (
                          <>
                            {msg.sql_text && (
                              <Box sx={{ mb: 1 }}>
                                <Button
                                  onClick={() => {
                                    // Toggle collapse state for this specific message
                                    setMessageFeedback(prev => ({
                                      ...prev,
                                      [msg.id]: {
                                        ...prev[msg.id],
                                        sqlExpanded: !prev[msg.id]?.sqlExpanded
                                      }
                                    }));
                                  }}
                                  size="small"
                                  sx={{
                                    p: 0.5,
                                    textTransform: 'none',
                                    color: 'grey',
                                    borderColor: 'grey',
                                    fontSize: '10px',
                                  }}
                                  variant='outlined'

                                  startIcon={
                                    <Icon
                                      icon={messageFeedback[msg.id]?.sqlExpanded
                                        ? "mdi:chevron-down"
                                        : "mdi:chevron-right"}
                                      width={14}
                                      height={14}
                                    />
                                  }
                                >
                                  SQL Query Executed
                                </Button>
                                <Collapse in={messageFeedback[msg.id]?.sqlExpanded || false}>
                                  <Typography variant="body1" sx={{ color: 'grey', whiteSpace: 'pre-wrap', pl: 2 }}>
                                    {formatMessageText(msg.sql_text)}
                                  </Typography>
                                </Collapse>
                              </Box>
                            )}
                            {console.log(msg)}
                            {msg.hasError ? (
                              <Alert
                                severity="error"
                                sx={{ whiteSpace: 'pre-wrap', mt: 1 }}
                              >
                                {msg.text}
                              </Alert>
                            ) : (
                              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                {formatMessageText(msg.text)}
                              </Typography>)}
                          </>
                        )}
                        {msg.attachments?.length > 0 && (
                          <Box sx={{ mt: 0 }}>
                            {msg.attachments.map((file, i) => (
                              <Paper
                                key={i}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  p: '4px 8px',
                                  backgroundColor: 'background.paper',
                                  borderRadius: '5px',
                                }}
                              >
                                <Icon
                                  icon={file.name.endsWith('.csv') ? 'vscode-icons:file-type-csv' : 'vscode-icons:file-type-excel'}
                                  width={16}
                                  height={16}
                                />
                                <Typography variant="caption">{file.name}</Typography>
                              </Paper>
                            ))}
                          </Box>
                        )}                        {(msg.sender === 'ai' && msg?.type?.includes('table') && msg.tableData) && (
                          <RenderTable
                            tableData={msg.tableData}
                            sessionId={msg.session_id || sessionId}
                            questionId={msg.question_id}
                          />
                        )}                        {/* Message actions - like/dislike for normal responses, copy for all */}
                        {msg.sender === 'ai' && !msg.isLoading && (
                          <Box sx={{ display: 'flex', justifyContent: 'left', mt: 1 }}>
                            {/* Like/Dislike buttons only for non-aborted messages */}
                            {(!msg.aborted &&
                              !msg.text?.includes('Request was aborted') &&
                              !msg.text?.includes('Request was cancelled')) && (
                                <Box sx={{ display: 'flex', position: 'relative' }}>
                                  {feedbackLoading[msg.id] ? (
                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minWidth: '80px',
                                      height: '40px'
                                    }}>
                                      <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
                                    </Box>
                                  ) : (
                                    <>
                                      <IconButton
                                        size="small"
                                        sx={{
                                          color: messageFeedback[msg.id]?.liked ? 'primary.main' : 'text.secondary',
                                          position: 'relative'
                                        }}
                                        onClick={() => handleLike(msg.id)}
                                      >
                                        <Tooltip
                                          title={feedbackErrors[msg.id] ? feedbackErrors[msg.id] : "Like"}
                                          arrow
                                        >
                                          <Box sx={{ position: 'relative' }}>
                                            <Icon icon={messageFeedback[msg.id]?.liked ? "fluent:thumb-like-20-filled" : "fluent:thumb-like-20-regular"} />
                                            {feedbackErrors[msg.id] && (
                                              <Icon
                                                icon="material-symbols:warning"
                                                width={12}
                                                height={12}
                                                style={{
                                                  position: 'absolute',
                                                  top: -2,
                                                  right: -2,
                                                  color: '#ff9800'
                                                }}
                                              />
                                            )}
                                          </Box>
                                        </Tooltip>
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        sx={{
                                          color: messageFeedback[msg.id]?.disliked ? 'error.main' : 'text.secondary',
                                          position: 'relative'
                                        }}
                                        onClick={() => handleDislike(msg.id)}
                                      >
                                        <Tooltip
                                          title={feedbackErrors[msg.id] ? feedbackErrors[msg.id] : "Dislike"}
                                          arrow
                                        >
                                          <Box sx={{ position: 'relative' }}>
                                            <Icon icon={messageFeedback[msg.id]?.disliked ? "fluent:thumb-dislike-20-filled" : "fluent:thumb-dislike-20-regular"} />
                                            {feedbackErrors[msg.id] && (
                                              <Icon
                                                icon="material-symbols:warning"
                                                width={12}
                                                height={12}
                                                style={{
                                                  position: 'absolute',
                                                  top: -2,
                                                  right: -2,
                                                  color: '#ff9800'
                                                }}
                                              />
                                            )}
                                          </Box>
                                        </Tooltip>
                                      </IconButton>
                                    </>
                                  )}
                                </Box>
                              )}

                            {/* Copy button for all AI messages */}
                            <IconButton
                              size="small"
                              sx={{ color: 'text.secondary' }}
                              onClick={() => handleCopy(msg.text, msg.id)}
                            >
                              {copiedMessageIds[msg.id] ? (
                                <Tooltip title="Copied" arrow>
                                  <Icon icon="charm:tick" color="#4caf50" />
                                </Tooltip>
                              ) : (
                                <Tooltip title="Copy to clipboard" arrow>
                                  <Icon icon="solar:copy-line-duotone" />
                                </Tooltip>
                              )}
                            </IconButton>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  ))}
                </React.Fragment>))}

              {/* Show ChatLoader when processing */}
              {isLoading && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                  <ChatLoader message="Processing your query..." />
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>
          </Box>
        )}

        {/* Input & Attachment Area */}
        <Box
          sx={{
            pt: 1,
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            mb: 2,
            alignItems: 'center',
            flexShrink: 0,
          }}
        >          {/* Empty-state Logo */}
          {messages.length === 0 && !isLoading && (
            <Box
              sx={{
                display: 'flex',
                mb: 2,
                width: isMobile ? '100%' : '60%',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              {inputValue === '' && messages.length === 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    mt: 3,
                    flexDirection: 'row',
                    width: isMobile ? '100%' : '60%',
                    mb: 1,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {/* Suggested prompts would go here */}
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <img src="/ethos.svg" alt="Logo" width={50} height={50} />
                <Typography
                  variant="h6"
                  sx={{ fontSize: 30, fontWeight: 'bold', color: 'text.primary' }}
                >
                  Parts360
                </Typography>
              </Box>
            </Box>
          )}          <Paper
            elevation={1}
            data-tour="message-input"
            sx={{
              px: 2,
              py: 1,
              borderRadius: '12px',
              borderWidth: `1px`,
              borderStyle: 'solid',
              borderColor: theme => theme.palette.background.chatBorder,
              width: isMobile ? '90%' : '60%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 1 }}>              <TextField
              inputRef={inputRef}
              variant="standard"
              fullWidth
              multiline
              maxRows={5}
              placeholder={hasPortalAccess === false ? "Portal access required to send messages" : "Message Parts360"}
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={isLoading || hasPortalAccess === false}
              InputProps={{
                disableUnderline: true,
                sx: { color: 'text.primary' }
              }}
              sx={{ mr: 1 }}
            />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {(
                <Box>
                  <Button
                    size="small"
                    startIcon={<Icon icon="icomoon-free:attachment" width={16} height={16} />}
                    onClick={handleAddAttachmentClick}
                    disabled={isLoading || attachments.length === 1}
                    sx={{ 
                      textTransform: 'none', 
                      color: 'text.secondary', 
                      mr: 1,
                      px: { xs: 1, sm: 1.5 },
                      minWidth: { xs: '40px', sm: 'auto' }
                    }}
                    data-tour="upload-excel"
                  >
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      Upload Parts Excel
                    </Box>
                  </Button>
                  {/* Pivot Button */}
                  <Tooltip title={isPivotSelected ? "Pivot enabled" : "Enable pivot"} arrow>
                    <Button
                      size="small"
                      onClick={handlePivotToggle}
                      disabled={isLoading || hasPortalAccess === false}
                      sx={{
                        textTransform: 'none',
                        color: isPivotSelected ? 'white' : 'text.secondary',
                        backgroundColor: isPivotSelected ? '#2065D1' : 'transparent',
                        border: `1px solid ${isPivotSelected ? '#2065D1' : '#e0e0e0'}`,
                        borderRadius: '16px',
                        fontWeight: 500,
                        fontSize: '0.65rem',
                        '&:hover': {
                          backgroundColor: isPivotSelected ? '#1753b0' : '#f5f5f5',
                          borderColor: isPivotSelected ? '#1753b0' : '#bdbdbd'
                        },
                        '&:disabled': {
                          opacity: 0.5,
                          cursor: 'not-allowed'
                        },
                        transition: 'all 0.2s ease',
                        ml: 0.5,
                        px: 0.7,
                        py: 0.3,
                        minHeight: '32px'
                      }}
                      aria-label={isPivotSelected ? "Disable pivot mode" : "Enable pivot mode"}
                    >
                      <Icon 
                        icon="mdi:pivot-table" 
                        width={16} 
                        height={16}
                        style={{ marginRight: '4px' }}
                      />
                      {isPivotSelected ? "Pivot" : "Pivot"}
                    </Button>
                  </Tooltip>
                </Box>)}
              <Box>
                <FormControl
                  variant="standard"
                  data-tour="agent-selector"
                  sx={{
                    minWidth: 90,
                    mr: 1,
                    border: '0.5px solid #bdbdbd',
                    borderRadius: '14px',
                    position: 'relative',
                    p: 0.2,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      right: 0,
                      width: '1px',
                      backgroundColor: theme => theme.palette.divider,
                      transform: 'translateY(-50%)'
                    }
                  }}
                >
                  <Select
                    value={agentType}
                    onChange={(e) => setAgentType(e.target.value)}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Agent type' }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '8px',
                          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                          mt: 0.5,
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'right',
                      },
                      transformOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      }
                    }}
                    sx={{
                      fontSize: '0.6rem',
                      color: 'text.primary',
                      '& .MuiSelect-select': {
                        paddingY: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                      },
                      '&:before': { borderBottom: 'none' },
                      '&:after': { borderBottom: 'none' },
                      '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                      '& .MuiSelect-icon': {
                        color: 'text.secondary',
                        transition: 'transform 0.2s',
                      },
                      '&.Mui-focused .MuiSelect-icon': {
                        transform: 'rotate(180deg)',
                      }
                    }}
                  >
                    <MenuItem value="sqlagent" sx={{
                      borderRadius: '4px',
                      display: 'flex',
                      fontSize: '0.6rem',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Icon icon="mdi:database" width={16} height={16} style={{ color: '#4caf50' }} />
                      <span>SQL Agent</span>
                    </MenuItem>
                    <MenuItem value="aisearch" sx={{
                      borderRadius: '4px',
                      display: 'flex',
                      fontSize: '0.6rem',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Icon icon="mdi:magnify" width={16} height={16} style={{ color: '#2196f3' }} />
                      <span>AI Search</span>
                    </MenuItem>
                  </Select>
                </FormControl>

                {console.log(" is loading: ", isLoading, currentRequestId, " current request id: ", currentRequestId)}
                  {(isLoading && currentRequestId != null) ? (
                    <Tooltip title="Stop response">
                      <IconButton
                        size="small"
                        onClick={handleStop}
                        sx={{ color: 'error.main' }}
                      >
                        <Icon icon="mdi:stop-circle" width={20} height={20} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={handleSend}
                      disabled={(!inputValue.trim() && attachments.length === 0) || isLoading || hasPortalAccess === false}
                      sx={{ color: 'text.secondary' }}
                    >
                      <Icon icon="fluent-color:send-16" />
                    </IconButton>
                  )}
              </Box>
            </Box>
            {attachments.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  width: isMobile ? '100%' : '60%',
                  flexDirection: 'row',
                }}
              >
                {attachments.map((file, idx) => (<Paper
                  key={idx}
                  elevation={3}
                  variant='outlined'
                  sx={{
                    alignItems: 'center',
                    p: '3px 6px',
                    borderRadius: '12px',
                    display: 'flex',
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      pointerEvents: 'none',
                    }}
                  >
                    {isLoading && idx === 0 ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Icon
                        icon={file.name.endsWith('.csv') ? 'vscode-icons:file-type-csv' : 'vscode-icons:file-type-excel'}
                        width={16}
                        height={16}
                      />
                    )}
                  </IconButton>
                  <Typography variant="caption" sx={{ mr: 1 }}>
                    {file.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAttachment(idx)}
                    disabled={isLoading || hasPortalAccess === false}
                  >
                    <Icon icon="material-symbols:close" width={16} height={16} />
                  </IconButton>
                </Paper>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
      <Dialog
        open={feedbackDialog}
        onClose={handleDialogClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: '400px',
            maxWidth: '600px',
            p: 1,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          '& .MuiTypography-root': {
            fontWeight: 600
          }
        }}>
          <Icon icon="fluent:thumb-dislike-20-filled" color="#f44336" width={24} height={24} />
          <Typography variant="h6">What could be improved?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Please tell us what was incorrect or missing in our response. Your feedback helps us improve.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder="Your feedback..."
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: 'background.paper',
                transition: 'box-shadow 0.2s',
                '&:hover': {
                  borderColor: '#2065D1'
                },
                '&.Mui-focused': {
                  borderColor: '#2065D1',
                  boxShadow: '0 0 0 3px rgba(32, 101, 209, 0.08)'
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{
          pt: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1
        }}>
          <Button
            onClick={handleDialogClose}
            sx={{
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>          <Button
            variant='contained'
            onClick={handleFeedbackSubmit}
            startIcon={feedbackLoading[currentMessageId] ? <CircularProgress size={16} color="inherit" /> : <Icon icon="material-symbols:send-outline" />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              bgcolor: '#2065D1',
              '&:hover': {
                bgcolor: '#1753b0'
              },
              fontWeight: 500,
              boxShadow: '0 4px 8px rgba(32, 101, 209, 0.24)'
            }}
            disabled={!feedbackText.trim() || feedbackLoading[currentMessageId]}
          >
            {feedbackLoading[currentMessageId] ? 'Submitting...' : 'Submit'}
          </Button></DialogActions>
      </Dialog>      {/* Snackbar for new conversation notifications */}
      <Snackbar
        open={newConversationSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setNewConversationSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNewConversationSnackbar({ open: false, message: '' })}
          severity="info"
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': {
              color: '#2065D1'
            }
          }}
        >
          {newConversationSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Snackbar for feedback errors */}
      <Snackbar
        open={feedbackSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setFeedbackSnackbar({ ...feedbackSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setFeedbackSnackbar({ ...feedbackSnackbar, open: false })}
          severity={feedbackSnackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': {
              color: feedbackSnackbar.severity === 'error' ? '#f44336' : '#2065D1'
            }
          }}
        >
          {feedbackSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatArea;